import { ChangeEvent, ClipboardEvent, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import { IProduct } from '@/models/products.model';
import { ProductsAPI } from '@/api';
import { CloseIcon } from '@/icons';

import { mergeUniqueArticles, parseArticles } from './helpers';
import {
  StyledFindButton,
  StyledLabel,
  StyledSearchFieldWrap,
  StyledSearchRow,
  StyledSuggestionItem,
  StyledSuggestionMeta,
  StyledSuggestionSku,
  StyledSuggestions,
  StyledTag,
  StyledTagField,
  StyledTagInput,
} from './BulkAddVariationsContainerStyles';

type TSkuArticlesInputProps = {
  articles: string[];
  onArticlesChange: (articles: string[]) => void;
  onFind: (articles: string[]) => void;
  isSearching?: boolean;
};

const SkuArticlesInput = ({ articles, onArticlesChange, onFind, isSearching }: TSkuArticlesInputProps) => {
  const { t } = useTranslation(['products']);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<IProduct[]>([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);

  const fetchSuggestions = useRef(
    debounce(async (value: string) => {
      const query = value.trim();
      if (!query) {
        setSuggestions([]);
        setIsSuggestionsOpen(false);
        return;
      }

      try {
        const response = await ProductsAPI.getProductsList({ article: query, limit: 10 });
        setSuggestions(response.data?.data ?? []);
        setIsSuggestionsOpen(true);
      } catch {
        setSuggestions([]);
        setIsSuggestionsOpen(false);
      }
    }, 300),
  ).current;

  useEffect(() => {
    return () => {
      fetchSuggestions.cancel();
    };
  }, [fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        setIsSuggestionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addArticles = (incoming: string[]) => {
    if (!incoming.length) {
      return;
    }
    onArticlesChange(mergeUniqueArticles(articles, incoming));
  };

  const handleDraftChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (/[,;\n\r]/.test(value)) {
      addArticles(parseArticles(value));
      setDraft('');
      setSuggestions([]);
      setIsSuggestionsOpen(false);
      fetchSuggestions.cancel();
      return;
    }

    setDraft(value);
    fetchSuggestions(value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const next = parseArticles(draft);
      if (next.length) {
        addArticles(next);
        setDraft('');
        setSuggestions([]);
        setIsSuggestionsOpen(false);
      }
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!/[,;\n\r]/.test(text)) {
      return;
    }

    event.preventDefault();
    addArticles(parseArticles(`${draft}${text}`));
    setDraft('');
    setSuggestions([]);
    setIsSuggestionsOpen(false);
  };

  const handleRemove = (event: MouseEvent<HTMLButtonElement>) => {
    const value = event.currentTarget.getAttribute('data-value');
    onArticlesChange(articles.filter((item) => item !== value));
  };

  const flushDraft = (): string[] => {
    const pending = parseArticles(draft);
    const next = mergeUniqueArticles(articles, pending);
    if (pending.length) {
      onArticlesChange(next);
      setDraft('');
      setSuggestions([]);
      setIsSuggestionsOpen(false);
    }
    return next;
  };

  const handleSelectSuggestion = (product: IProduct) => {
    addArticles([product.sku]);
    setDraft('');
    setSuggestions([]);
    setIsSuggestionsOpen(false);
  };

  return (
    <StyledSearchRow>
      <StyledLabel htmlFor="bulk-variation-articles">{t('product_variations_bulk_search_label')}</StyledLabel>
      <StyledSearchFieldWrap ref={wrapRef}>
        <StyledTagField>
          {articles.map((article) => (
            <StyledTag key={article}>
              {article}
              <button type="button" data-value={article} onClick={handleRemove} aria-label={article}>
                <CloseIcon />
              </button>
            </StyledTag>
          ))}
          <StyledTagInput
            id="bulk-variation-articles"
            value={draft}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={t('product_variations_bulk_search_placeholder')}
            autoComplete="off"
          />
        </StyledTagField>
        {isSuggestionsOpen && suggestions.length > 0 && (
          <StyledSuggestions>
            {suggestions.map((product) => (
              <li key={product.id}>
                <StyledSuggestionItem type="button" onClick={() => handleSelectSuggestion(product)}>
                  <StyledSuggestionSku>{product.sku}</StyledSuggestionSku>
                  <StyledSuggestionMeta>
                    {product.name}
                    {product.external_id ? ` · ${product.external_id}` : ''}
                  </StyledSuggestionMeta>
                </StyledSuggestionItem>
              </li>
            ))}
          </StyledSuggestions>
        )}
      </StyledSearchFieldWrap>
      <StyledFindButton
        type="default"
        onClick={() => onFind(flushDraft())}
        loading={isSearching}
        disabled={!articles.length && !draft.trim()}
      >
        {t('product_variations_bulk_find')}
      </StyledFindButton>
    </StyledSearchRow>
  );
};

export default SkuArticlesInput;
